import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import NavIcon from "./NavIcon";
import "./Sidebar.css";

/*
 * The left navigation every module shares. A module passes data, not markup:
 *
 *   sections: [{ title?, items: [item] }]
 *   admin:    { label, icon, items: [item] }   — collapsible group at the bottom
 *
 *   item: {
 *     to, label, icon,             icon is a NavIcon name
 *     end?,                        exact-match the route, as NavLink's `end`
 *     match?: ["/prefix/"],        other paths that should also light it up
 *     permission?: string|string[] any-of; admins always pass
 *     adminOnly?,                  never granted through a role
 *     visible?: (ctx) => bool,     anything else, e.g. a module flag
 *     badge?: key into `badges`
 *   }
 */

function hasPermission(user, permission) {
  if (!user) return false;
  if (user.isAdmin || !permission) return true;
  const required = Array.isArray(permission) ? permission : [permission];
  return required.some((p) => user.permissions?.includes(p));
}

export function isItemVisible(item, ctx) {
  if (item.adminOnly) return Boolean(ctx.user?.isAdmin);
  if (item.visible && !item.visible(ctx)) return false;
  return hasPermission(ctx.user, item.permission);
}

export default function Sidebar({ sections, admin, collapsed, badges = {}, id, onToggle }) {
  const auth = useAuth();
  const location = useLocation();
  const ctx = { user: auth.user, invoicingEnabled: auth.invoicingEnabled, enabledModules: auth.enabledModules };
  const [adminOpen, setAdminOpen] = useState(() =>
    (admin?.items || []).some((item) => location.pathname.startsWith(item.to)),
  );

  const renderLink = (item) => {
    const count = item.badge ? badges[item.badge] || 0 : 0;
    const alsoActive = item.match?.some((prefix) => location.pathname.startsWith(prefix));
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) => `sb-link ${isActive || alsoActive ? "active" : ""}`.trim()}
      >
        <span className="sb-icon-wrap">
          <NavIcon name={item.icon} />
          {count > 0 && collapsed && <span className="sb-badge-dot" aria-hidden="true" />}
        </span>
        <span className="sb-label">{item.label}</span>
        {count > 0 && (
          <span className="sb-badge">
            {count > 99 ? "99+" : count}
            <span className="sb-visually-hidden"> unread</span>
          </span>
        )}
      </NavLink>
    );
  };

  const adminItems = (admin?.items || []).filter((item) => isItemVisible(item, ctx));

  return (
    <aside id={id} className={`app-sidebar ${collapsed ? "collapsed" : ""}`.trim()} aria-label="Main navigation">
      <nav className="sb-scroll">
        {sections.map((section, idx) => {
          const items = section.items.filter((item) => isItemVisible(item, ctx));
          if (items.length === 0) return null;
          return (
            <div className="sb-section" key={section.title || idx}>
              {section.title && <div className="sb-section-title">{section.title}</div>}
              {items.map(renderLink)}
            </div>
          );
        })}

        {adminItems.length > 0 && (
          <div className="sb-section sb-admin">
            <button
              type="button"
              className="sb-link sb-admin-toggle"
              onClick={() => setAdminOpen((v) => !v)}
              aria-expanded={adminOpen}
              title={collapsed ? admin.label : undefined}
            >
              <span className="sb-icon-wrap">
                <NavIcon name={admin.icon || "settings"} />
              </span>
              <span className="sb-label">{admin.label}</span>
              <span className={`sb-chevron ${adminOpen ? "open" : ""}`.trim()}>
                <NavIcon name="chevron" />
              </span>
            </button>
            {adminOpen && <div className="sb-admin-items">{adminItems.map(renderLink)}</div>}
          </div>
        )}
      </nav>

      {/* Desktop only (hidden in the phone drawer by CSS, which closes with
          the backdrop, Escape or the menu button instead). */}
      {onToggle && (
        <div className="sb-footer">
          <button
            type="button"
            className="sb-collapse-toggle"
            onClick={onToggle}
            aria-controls={id}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <NavIcon name="collapse" />
          </button>
        </div>
      )}
    </aside>
  );
}
